import { useEffect, useState } from 'react'
import { ModalBody, ModalFooter } from 'react-bootstrap'
import { ReportesCumplimiento } from '../../autorizaciones/components/reportes/ReportesCumplimiento'
import { FileDropZone } from '../../../components/forms/dropzone/fileDropZone.type'
import { Tarea, TTareaComentario, style, useTareaComentarioForm } from '../models'
import { Alert } from '../../../components/alerts/Alert'
import { SubmitHandler } from 'react-hook-form'
import {
  useActualizarTareaMutation,
  useGuardarReportesMutation,
  useRecuperarReportesPorIdRelacionadoMutation,
} from '../../../redux/api'
import toast from 'react-hot-toast'
import { Cargando } from '../../../components/layout/Cargando'
import { AppConstantes } from '../../../utils/Constantes'
import { useDispatch } from 'react-redux'
import { cargarReportes } from '../../../redux/slices/reportes'
import Swal from 'sweetalert2'
import { alertUsuarios } from '../../usuarios/components'
import ComponentePrivado from '../../../components/roles/ComponentePrivado'
import { Roles } from '../../../utils/Roles'
import { Reporte } from '../../../models'
import FormTextArea from '../../../components/forms/textArea/FormTextArea'

const TITULO_TAREA_SUCCESS = 'Tarea actualizada'

type Props = {
  tarea: Tarea
  regresar: (siguiente: boolean) => void
  idTarea: string
  refetch: () => void
  refetchConsulta?: () => void
  setTareaFinalizada: (arg: Tarea) => void
  estatus: string
  cerrar: () => void
}

export const ComentariosReportes = ({
  tarea,
  regresar,
  idTarea,
  estatus,
  refetch,
  refetchConsulta,
  setTareaFinalizada,
  cerrar,
}: Props) => {
  const dispatch = useDispatch()
  const [arrArchivos, setArrArchivos] = useState([] as FileDropZone[])
  const formTareaComentario = useTareaComentarioForm()
  const [guardarReportes, { isLoading: isloadingGuardarReportes }] = useGuardarReportesMutation()
  const [recuperarReportes, { isLoading }] = useRecuperarReportesPorIdRelacionadoMutation()
  const [actualizarTarea, { isLoading: isLoadingActualizar, error: errorActualizar }] =
    useActualizarTareaMutation()
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = formTareaComentario

  useEffect(() => {
    tarea?.comentarioCierre && setValue('comentarioCierre', tarea?.comentarioCierre)

    // Hidrata el slice `reportes` con la evidencia ya asociada a la tarea.
    // Esto permite que el componente `ReportesCumplimiento` muestre adjuntos existentes
    // y se combine con archivos nuevos sin duplicarlos.
    recuperarReportes(tarea.idTarea)
      .unwrap()
      .then((resp: Reporte[]) => {
        dispatch(cargarReportes(resp))
      })
      .catch(console.error)
  }, [tarea?.comentarioCierre])

  const onSubmit: SubmitHandler<TTareaComentario> = (data: TTareaComentario) => {
    // Flujo de cierre de tarea (COMPLETADA):
    // 1) Actualiza la tarea con comentario de cierre.
    // 2) Si hay evidencia nueva, sube archivos vía `FormData`.
    // 3) Refresca consulta/listado y vuelve a cargar reportes asociados.
    const tareaFinalizada: Tarea = {
      ...tarea,
      comentarioCierre: data.comentarioCierre,
    }
    actualizarTarea(tareaFinalizada)
      .unwrap()
      .then(() => {
        refetchConsulta && refetchConsulta()
        setTareaFinalizada(tareaFinalizada)
        refetch()

        const formData = new FormData()
        // Contrato backend de carga de reportes: requiere id_relacionado + tipo + desc_tipo.
        formData.append('id_relacionado', tarea.idTarea)
        formData.append('tipo', AppConstantes.CVE_REPORTE_TIPO_TAREA)
        formData.append('desc_tipo', AppConstantes.DESC_REPORTE_TIPO_TAREA)

        if (arrArchivos && arrArchivos.length > 0) {
          arrArchivos.forEach((a) => {
            if (a.isNew && a.file) {
              // Sólo se envían archivos nuevos; los existentes permanecen asociados.
              formData.append('archivos', a.file, a.file.name)
            }
          })
          guardarReportes(formData)
            .unwrap()
            .then(() => {
              toast.success(TITULO_TAREA_SUCCESS, { style })
              recuperarReportes(tarea.idTarea)
                .unwrap()
                .then((resp: Reporte[]) => {
                  dispatch(cargarReportes(resp))
                  setArrArchivos([])
                })
                .catch(console.error)
            })
            .catch(() => {
              // La tarea ya quedó actualizada; si falla evidencia se mantiene el modal
              // para permitir reintentar la carga sin perder el comentario.
              Swal.fire({
                title: TITULO_TAREA_SUCCESS,
                icon: 'info',
                html: `Existen algunos documentos sin guardar, verifíquelos y actualice de nuevo.`,
                showCancelButton: true,
                confirmButtonText: 'Seguir editando',
                cancelButtonText: 'Cancelar',
                allowOutsideClick: false,
                allowEscapeKey: false,
              }).then(() => {})
            })
        } else {
          toast.success(TITULO_TAREA_SUCCESS, { style })
        }
      })
      .catch(() => {
        // Error al actualizar tarea: se delega a helper que muestra alertas según el error API.
        alertUsuarios(true, handleSubmit, errorActualizar)
      })
  }

  return (
    <>
      <ModalBody
        className={`${
          isLoadingActualizar || isloadingGuardarReportes || isLoading
            ? AppConstantes.OVERLAY_BLOCK
            : ''
        }`}>
        <Alert
          mensaje={`Al establecer una tarea completada es necesario ingresar un comentario de cierre, y si es necesario, cargar documentos generados.`}
          fs={5}
          icono='fa fa-exclamation-circle fs-2x'
          titulo='Tarea completada'
          color={'dodger-blue'}
        />
        <form id='form-comentario' onSubmit={handleSubmit(onSubmit)}>
          <div className='d-flex flex-column pt-2'>
            <FormTextArea
              nombreCampo='comentarioCierre'
              register={register}
              isObligatorio={true}
              nombre='Comentario de la tarea'
              defaultValue={tarea.comentarioCierre}
              errors={errors}
              placeholder='Escribe un comentario'
              maxLength={1500}
              rows={4}></FormTextArea>
          </div>
        </form>
        <div className={`min-h-100px pt-5 pb-2 ${isLoading ? AppConstantes.OVERLAY_BLOCK : ''}`}>
          <label className='form-label fs-3'>Evidencia documental</label>
          <ReportesCumplimiento
            arrArchivos={arrArchivos}
            setArrArchivos={setArrArchivos}
            idRelacionado={idTarea}
            textoTipoArchivo='No hay ningun archivo cargado'
          />
        </div>
        {(isLoadingActualizar || isLoading) && <Cargando mensaje='Cargando tarea' />}
      </ModalBody>
      <ModalFooter
        className={`${
          isLoadingActualizar || isloadingGuardarReportes ? AppConstantes.OVERLAY_BLOCK : ''
        }`}>
        <div className='d-flex justify-content-between flex-grow-1'>
          <div>
            <button
              disabled={isLoadingActualizar || isloadingGuardarReportes}
              className='btn btn-light-primary'
              onClick={() => {
                regresar(false)
              }}>
              <span>
                <i className='me-2 fa-solid fa-arrow-left'></i>
              </span>
              Regresar
            </button>
          </div>
          <div>
            <button
              disabled={isLoadingActualizar || isloadingGuardarReportes}
              type='reset'
              className='btn text-hover-dark btn-cancelar'
              onClick={() => {
                cerrar()
              }}>
              Cancelar
            </button>
            <ComponentePrivado rolMostrarPorPermiso={[Roles.roleTareasCrear]}>
              <button
                disabled={isLoadingActualizar}
                form='form-comentario'
                className='btn btn-primary p-3'
                type='submit'>
                Confirmar
              </button>
            </ComponentePrivado>
          </div>
        </div>
      </ModalFooter>
    </>
  )
}
